import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EdpURIs } from 'src/app/endpoints/edpURIs';
import { OrderReturn } from 'src/app/models/order/order-return.model';
import { BlackBoxWarningsRequest } from '../../models/report/black-box-warnings/black-box-warnings-request.model';
import { BlackBoxWarningsSortOrder } from '../../models/report/black-box-warnings/black-box-warnings-sort-order';
import { ReportFormat } from '../../models/report/report-formats/report-format.model';
import { ApiResponseService } from './api-response.service';
import { VisionApiService } from './vision-api.service';
import { DrugInteractionRequest } from 'src/app/models/report/drug-interaction/drug-interaction-request.model';


@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor(
    private api: VisionApiService,
    private apiResponseService: ApiResponseService
  ) {
  }

  public getPatientLiteralsByCategoryReport(facilityId: string, literalOrderCategoryCodes: string[]): Observable<Blob> {
    const body = {
      facilityId: facilityId,
      literalOrderCategoryCodes: literalOrderCategoryCodes,
      applySigTranslation: true
    };

    return this.api.postDownload<Blob>(EdpURIs.getPatientLiteralsByCategoryReport, body)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error printing patient literal order by category report.');
          return of(undefined);
        })
      );
  }

  public getAntimicrobialDaysOfTherapyReport(facilityIds: string[], startDate: string, endDate: string): Observable<Blob> {
    const body = {
      facilityIds: facilityIds,
      startDate: startDate,
      endDate: endDate
    };

    return this.api.postDownload<Blob>(EdpURIs.getAntimicrobialDaysOfTherapyReport, body)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving the antimicrobial days of therapy report.');
          return of(undefined);
        })
      );
  }

  public getLeaveOfAbsenceReport(facilityId: string, printMonographs: boolean, orders: OrderReturn[]): Observable<Blob> {
    const body = {
      facilityId: facilityId,
      printMonographs: printMonographs,
      orders: orders
    };

    return this.api.postDownload<Blob>(EdpURIs.getLeaveOfAbsenceReport, body)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving leave of absence report.');
          return of(undefined);
        })
      );
  }

  public getDrugInteractionReport(request: DrugInteractionRequest): Observable<Blob> {
    return this.api.postDownload<Blob>(EdpURIs.getDrugInteractionReport, request)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving drug interaction report.');
          return of(undefined);
        })
      );
  }

  public getBlackBoxWarnings(request: BlackBoxWarningsRequest): Observable<Blob> {
    return this.api.postDownload<Blob>(EdpURIs.getBlackBoxWarnings, request)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving black box warning report.');
          return of(undefined);
        })
      );
  }

  public getBlackBoxWarningsSortOrder(): Observable<BlackBoxWarningsSortOrder[]> {
    return this.api.get<BlackBoxWarningsSortOrder[]>(EdpURIs.getBlackBoxWarningsSortOrder)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving black box warnings sort order.');
          return of(undefined);
        })
      );
  }

  public getReportFormatCodes(formatType: string): Observable<ReportFormat[]> {
    const queryParams = { formatType: formatType };

    return this.api.get<ReportFormat[]>(EdpURIs.getReportFormatCodes, queryParams)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, `Error retrieving ${formatType} medical record format.`);
          return of(undefined);
        })
      );
  }

  public getReportFormatsByFacilityId(facilityId: string): Observable<any> {
    const queryParams = { facilityId: facilityId };

    return this.api.get<any[]>(EdpURIs.reportFormatsByFacility, queryParams)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error retrieving the formats for a single facility.');
          return of(undefined);
        })
      );
  }

  public changeReportFormat(facilityId: string, formatType: string, formatCode: string): Observable<any> {
    const request = {
      facilityId: facilityId,
      formatType: formatType,
      formatCode: formatCode
    };

    return this.api.put<any>(EdpURIs.reportFormatsByFacility, request)
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, `Error updating report format type`);
          return of(undefined);
        })
      );
  }
}
